import { LANDING_HEADER_HEIGHT } from '@/components/landing/landing-header.component';
import { CheckCircle, Search, Textsms } from '@mui/icons-material';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Container,
  Grid,
  Stack,
  Typography
} from '@mui/material';
import { IRIS_EMAIL, IRIS_PHONE_DISPLAY } from '@/constants/iris';
import { tDynamic } from '@/lib/i18n';
import { brandVars as brand } from '@/theme/theme';
import type { ElementType, FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';

type Step = {
  icon: ElementType;
  titleKey: string;
  descriptionKey: string;
  descriptionVars?: Record<string, string>;
};

const steps: Step[] = [
  {
    icon: Textsms,
    titleKey: 'landing:howItWorks.steps.forward.title',
    descriptionKey: 'landing:howItWorks.steps.forward.description',
    descriptionVars: { phone: IRIS_PHONE_DISPLAY, email: IRIS_EMAIL }
  },
  {
    icon: Search,
    titleKey: 'landing:howItWorks.steps.analyze.title',
    descriptionKey: 'landing:howItWorks.steps.analyze.description'
  },
  {
    icon: CheckCircle,
    titleKey: 'landing:howItWorks.steps.answer.title',
    descriptionKey: 'landing:howItWorks.steps.answer.description'
  }
];

export const LandingHowItWorks: FunctionComponent = () => {
  const { t } = useTranslation();

  return (
    <Box
      id="how-it-works"
      component="section"
      sx={(theme) => ({
        // Offset anchor scrolling by the fixed header height so the heading
        // isn't hidden underneath. scrollMarginTop is not a spacing-aware sx
        // key, so resolve LANDING_HEADER_HEIGHT (spacing units) to px here.
        scrollMarginTop: {
          xs: theme.spacing(LANDING_HEADER_HEIGHT.xs),
          sm: theme.spacing(LANDING_HEADER_HEIGHT.sm)
        },
        py: { xs: 8, sm: 10, lg: 12 },
        px: { xs: 2, sm: 3 }
      })}
    >
      <Container maxWidth="lg">
        <Stack sx={{ alignItems: 'center', mb: { xs: 6, sm: 8 } }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '1.75rem', sm: '2rem', lg: '2.25rem' },
              textAlign: 'center',
              mb: 2
            }}
          >
            {t('landing:howItWorks.title')}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: '1rem', sm: '1.125rem' },
              color: 'text.secondary',
              textAlign: 'center',
              maxWidth: 600
            }}
          >
            {t('landing:howItWorks.subtitle')}
          </Typography>
        </Stack>

        <Grid container spacing={3}>
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <Grid key={step.titleKey} size={{ xs: 12, sm: 6, lg: 4 }}>
                <Box sx={{ position: 'relative', height: '100%' }}>
                  {index < steps.length - 1 && (
                    <Box
                      sx={{
                        display: { xs: 'none', lg: 'block' },
                        position: 'absolute',
                        top: 48,
                        left: 'calc(50% + 2rem)',
                        width: 'calc(100% - 4rem)',
                        height: 2,
                        background: brand.gradients.connector
                      }}
                    />
                  )}
                  <Card
                    elevation={0}
                    sx={{
                      height: '100%',
                      bgcolor: 'background.paper',
                      borderRadius: 3,
                      border: '2px solid',
                      borderColor: 'grey.200',
                      transition: 'border-color 0.2s ease',
                      '&:hover': { borderColor: 'primary.main' },
                      position: 'relative',
                      zIndex: 1
                    }}
                  >
                    <CardContent
                      sx={{
                        p: { xs: 2.5, sm: 3 },
                        textAlign: 'center',
                        position: 'relative'
                      }}
                    >
                      <Avatar
                        sx={{
                          position: 'absolute',
                          top: { xs: 10, sm: 12 },
                          left: { xs: 10, sm: 12 },
                          width: 28,
                          height: 28,
                          bgcolor: 'primary.main',
                          fontSize: '0.875rem',
                          fontWeight: 600
                        }}
                      >
                        {index + 1}
                      </Avatar>
                      <Avatar
                        sx={{
                          width: 56,
                          height: 56,
                          bgcolor: 'primary.main',
                          mx: 'auto',
                          mb: 2
                        }}
                      >
                        <Icon sx={{ color: 'white', fontSize: 28 }} />
                      </Avatar>
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: { xs: '1.125rem', sm: '1.25rem' },
                          mb: 1.5
                        }}
                      >
                        {tDynamic(t, step.titleKey)}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          lineHeight: 1.6
                        }}
                      >
                        {tDynamic(t, step.descriptionKey, step.descriptionVars)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </Box>
  );
};
